import { currencySymbol, currencyWord } from './round-config'
import { mutate, type Ack } from './api'
import type { Clean, DayLedger, RoundDay, SchedulePayload } from './types'
import { el, isPastOrToday, money, parseMoney, todayISO } from './ui'
import { getMe, ownerDoesJobs, worksAlone } from './me'

// The day close-out (day_ledger), split out of the schedule screen so its
// money rules can be tested headlessly. One row per (date, worker); the API's
// POST is insert-only and revisions are version-guarded PATCHes.
//
// Attribution follows the diary and worker phone: a visit's name, else the
// worker out that day, else nobody.

// No seed names: a business's worker list is only ever the names it has
// actually used, never another business's staff hardcoded in as a fallback.
// A hardcoded ['liam','lewis','grant'] here once made it impossible to close
// out a day for the two MOST frequent workers in Dropby's own imported data
// (Joe: 48 ledger rows, Matt: 27) until this function read past the seed —
// worth keeping in mind if a brand-new business's list ever looks emptier
// than expected: it means nobody has closed out a day yet, not a bug.

// Everyone the round has ever used. The API supplies this across the WHOLE
// dataset (`workers`) — deriving it from the visible window meant a fortnight
// with no history offered nothing at all. Real data wins on capitalisation
// (first spelling seen keeps its case) and the list is deduped by lowercase
// so 'Liam' and 'liam' are one person, not two rows.
export function knownWorkers(p: SchedulePayload | null): string[] {
  const seen = new Map<string, string>() // lowercase key → display form
  for (const w of p?.workers ?? []) {
    const t = (w ?? '').trim()
    if (t) seen.set(t.toLowerCase(), t)
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b))
}

// ---- attribution (pure — no DOM past this line's callers) --------------------

export interface WorkerDayTotals {
  pence: number
  jobs: number
  // The payment split of that logged work, so the form can distinguish the
  // CURRENT payment status of completed work from anything received today.
  paidPence: number
  unpaidPence: number
  unknownPence: number
  unpriced: number
}

export interface DayAttribution {
  // key: worker trimmed + lowercased, so 'Liam' and 'liam' are one person
  byWorker: Map<string, WorkerDayTotals>
  unassigned: WorkerDayTotals
}

function emptyTotals(): WorkerDayTotals {
  return { pence: 0, jobs: 0, paidPence: 0, unpaidPence: 0, unknownPence: 0, unpriced: 0 }
}

// The diary roster is already scoped to the active business by /api/schedule.
export function visitWorker(cl: Clean, roster: readonly RoundDay[]): string {
  const named = cl.worker?.trim()
  if (named) return named
  return roster.find(r => r.date === cl.date)?.worker?.trim() ?? ''
}

export function canRemoveCloseout(row: DayLedger, day: DayAttribution): boolean {
  return totalsFor(day, row.worker).jobs === 0 &&
    [row.gross_pence, row.worker_cut_pence, row.our_cut_pence].every(n => n == null || n === 0)
}

export function unassignedVisits(cleans: Clean[], date: string, roster: readonly RoundDay[]): Clean[] {
  return cleans.filter(c => c.date === date && c.state === 'done' && !c.deleted_at && !visitWorker(c, roster))
}

export async function giveVisits(visits: Clean[], worker: string): Promise<void> {
  const reasons: (string | undefined)[] = []
  for (const c of visits) {
    const ack = await mutate({ label: `Give job to ${worker}`, method: 'PATCH', path: `/api/cleans/${c.id}`,
      body: { version: c.version, worker } })
    if (!ack.ok && ack.status >= 400 && ack.status < 500) reasons.push(ack.error)
  }
  if (reasons.length) {
    const reason = reasons[0]
    const message = `${reasons.length} job${reasons.length === 1 ? '' : 's'} could not be given to ${worker}`
    window.alert(reason && reasons.every(error => error === reason)
      ? `${message}: ${reason}` : `${message}. Reload and try again.`)
  }
}

export function assignmentChoices(date: string, rows: DayLedger[], ctx: CloseoutContext): string[] {
  const unique = (names: string[]) => {
    const seen = new Map<string, string>()
    for (const name of names) {
      const trimmed = name.trim()
      if (trimmed && !seen.has(trimmed.toLowerCase())) seen.set(trimmed.toLowerCase(), trimmed)
    }
    return [...seen.values()]
  }
  const counts = new Map<string, number>()
  for (const visit of ctx.cleans) {
    if (visit.state !== 'done' || visit.deleted_at) continue
    const key = visitWorker(visit, ctx.roster).toLowerCase()
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const others = unique(ctx.workers).sort((a, b) =>
    (counts.get(b.toLowerCase()) ?? 0) - (counts.get(a.toLowerCase()) ?? 0) || a.localeCompare(b))
  const recent = [...ctx.roster].filter(r => r.date <= date && r.worker.trim()).sort((a, b) => b.date.localeCompare(a.date))[0]
  const owner = ownerDoesJobs() ? getMe()?.workerName?.trim() : undefined
  const moneyNames = unique(rows.map(r => r.worker ?? ''))
  if (owner && moneyNames.some(name => name.toLowerCase() === owner.toLowerCase())) {
    moneyNames.sort((a, b) => Number(b.toLowerCase() === owner.toLowerCase()) - Number(a.toLowerCase() === owner.toLowerCase()))
  }
  return unique([...moneyNames, recent?.worker ?? '', ...others])
    .map(name => owner && name.toLowerCase() === owner.toLowerCase() ? owner : name)
}

// What the day's logged cleans add up to, per worker, with unassigned work in
// its own bucket. Only state='done' rows count; other dates and states are
// ignored. Unpriced jobs add 0 to the pence totals but are counted, so a
// figure built from them is never presented as exact.
export function attributeDay(cleans: Clean[], date: string, roster: readonly RoundDay[] = []): DayAttribution {
  const day: DayAttribution = { byWorker: new Map(), unassigned: emptyTotals() }
  for (const c of cleans) {
    if (c.date !== date || c.state !== 'done' || c.deleted_at) continue
    const key = visitWorker(c, roster).toLowerCase()
    const t = key ? (day.byWorker.get(key) ?? emptyTotals()) : day.unassigned
    t.pence += c.amount_pence ?? 0
    t.jobs += 1
    if (c.amount_pence === null) t.unpriced += 1
    if (c.payment_status === 'paid') t.paidPence += c.amount_pence ?? 0
    else if (c.payment_status === 'unpaid') t.unpaidPence += c.amount_pence ?? 0
    else t.unknownPence += c.amount_pence ?? 0
    if (key) day.byWorker.set(key, t)
  }
  return day
}

// A one-person round's day (Liam, 19 Oct 2026): everything done that day, whatever name it was logged under, as one.
export function wholeDayTotals(day: DayAttribution): WorkerDayTotals {
  const t = emptyTotals()
  for (const w of [day.unassigned, ...day.byWorker.values()]) {
    t.pence += w.pence; t.jobs += w.jobs; t.unpriced += w.unpriced
    t.paidPence += w.paidPence; t.unpaidPence += w.unpaidPence; t.unknownPence += w.unknownPence
  }
  return t
}

export function totalsFor(day: DayAttribution, worker: string | null | undefined): WorkerDayTotals {
  const key = (worker ?? '').trim().toLowerCase()
  if (!key) return day.unassigned
  return day.byWorker.get(key) ?? emptyTotals()
}

// A day someone finished with Finish my day (day_closes, 0064), not reopened: /api/schedule's finished_days.
export interface FinishedDay { date: string; worker: string }

const nameKey = (name: string | null | undefined): string => (name ?? '').trim().toLowerCase()

// What a one-person round's own day total is for (Sol's and Sonnet's review, 19 Oct 2026: it counted days twice). The
// whole day while the day has no total at all; once it has any (one under an old name, a worker's from before), only
// the done work none of them covers, by the name on the work as Money matches it, never the whole day again. Work with
// no name is covered by any total, as Money counts it. `partial`: other totals already cover some of the day.
export function aloneDayBasis(day: DayAttribution, rows: readonly Pick<DayLedger, 'worker'>[]): { totals: WorkerDayTotals; partial: boolean } {
  if (!rows.length) return { totals: wholeDayTotals(day), partial: false }
  const closed = new Set(rows.map((r) => nameKey(r.worker)))
  const t = emptyTotals()
  for (const [key, w] of day.byWorker) {
    if (closed.has(key)) continue
    t.pence += w.pence; t.jobs += w.jobs; t.unpriced += w.unpriced
    t.paidPence += w.paidPence; t.unpaidPence += w.unpaidPence; t.unknownPence += w.unknownPence
  }
  return { totals: t, partial: true }
}

// Is a one-person round's day missing its total, by Money's own rule (functions/lib/analysis.ts, missing, alone): done
// work, by the name on it, with neither a total nor a Finish my day under that name; work with no name, with neither
// any total nor any finish that day. A day the owner finished is not missing (review, 19 Oct 2026).
export function aloneDayMissing(day: DayAttribution, rows: readonly Pick<DayLedger, 'worker'>[], finished: readonly Pick<FinishedDay, 'worker'>[]): boolean {
  const closed = new Set(rows.map((r) => nameKey(r.worker)))
  const done = new Set(finished.map((f) => nameKey(f.worker)))
  for (const [key, w] of day.byWorker) if (w.jobs > 0 && !closed.has(key) && !done.has(key)) return true
  return day.unassigned.jobs > 0 && !rows.length && !finished.length
}

// How a typed figure compares with the cleans logged for that worker. Null
// when there is nothing to compare against — a gross for work nobody logged
// as cleans is legitimate (cash jobs, one-off gutters), not a variance.
export function varianceNote(grossPence: number, logged: WorkerDayTotals): { text: string; warn: boolean } | null {
  if (logged.jobs === 0) return null
  if (grossPence === logged.pence && logged.unpriced === 0) return null
  const diff = grossPence - logged.pence
  const head =
    diff === 0
      ? `matches the ${logged.jobs} logged clean${logged.jobs === 1 ? '' : 's'}`
      : `${money(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} than the ${logged.jobs} logged clean${logged.jobs === 1 ? '' : 's'}`
  const tail = logged.unpriced > 0 ? ` — ${logged.unpriced} with no price, so the logged figure is a floor` : ''
  return { text: head + tail, warn: diff !== 0 || logged.unpriced > 0 }
}

// The payment split of the LOGGED work, worded so it can never read as money
// received today: there is no receipt date anywhere in the schema, so the
// close-out must not claim one. This is the status of the completed work, and
// the only figure that says "cash left the business today" is the worker cut.
export function paymentStatusNote(logged: WorkerDayTotals): string | null {
  if (logged.jobs === 0) return null
  const parts: string[] = []
  if (logged.paidPence > 0) parts.push(`${money(logged.paidPence)} already marked paid`)
  if (logged.unpaidPence > 0) parts.push(`${money(logged.unpaidPence)} unpaid`)
  if (logged.unknownPence > 0) parts.push(`${money(logged.unknownPence)} payment not recorded`)
  if (logged.unpriced > 0) parts.push(`${logged.unpriced} unpriced`)
  if (!parts.length) return null
  return `logged work by current status: ${parts.join(' · ')} — payment status of completed work, not cash taken today`
}

export type PrefillDecision = 'replace' | 'keep' | 'ask'

// Changing the worker swaps the prefill basis. A gross the user has TYPED is
// theirs: replacing it needs permission, and it is never wiped silently —
// not even when it doesn't parse (half-typed input is still input).
export function prefillDecision(currentValue: string, currentIsPrefill: boolean, prefillValue: string): PrefillDecision {
  if (prefillValue === '') return currentIsPrefill ? 'replace' : 'keep' // no logged work to offer for this worker
  if (currentIsPrefill) return 'replace' // the machine's own figure, not the user's
  if (currentValue.trim() === '') return 'replace' // an empty box has nothing to erase
  try {
    if (parseMoney(currentValue) === parseMoney(prefillValue)) return 'replace' // same figure either way
  } catch {
    return 'ask'
  }
  return 'ask'
}

// ---- forms --------------------------------------------------------------------

export interface CloseoutContext {
  cleans: Clean[] // the loaded window's cleans, any state — attribution filters
  roster: readonly RoundDay[]
  workers: string[] // everyone the round has ever used (knownWorkers)
  isEditing: () => boolean
  beginEdit: () => void
  afterEdit: () => void
  reload: () => void
  // Set once a missing day's form has been opened for him on this draw, so
  // only one opens by itself (see renderDayLedger).
  autoOpened?: boolean
  // The days finished with Finish my day in the window read (one-person rounds count them as totalled, as Money does).
  finished?: readonly FinishedDay[]
}

// The 4xx terminal case for fire-and-forget mutations (codex CRITICAL-2):
// mutate() drops invalid edits from the queue and returns the ack — if the
// caller ignores it, a rejection vanishes silently.
function alert4xx(ack: Ack): void {
  if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? 'rejected')
}

const UNASSIGNED = '' // ledger worker value for work nobody was recorded against

// `passive`: the form was opened FOR him (a missing day), not by him. It then
// takes the editing lock only once he touches it. Holding the lock from the
// moment it opened froze the whole diary: every move, confirm or Done after it
// waited for a redraw that never came while an untouched form sat on a past
// day (found 2026-09-24, when a moved job never reached its new day). An
// untouched form loses nothing by being drawn again; it opens again itself.
// `basis`: a one-person round's total, what it is for (aloneDayBasis).
function closeoutForm(date: string, workersLeft: string[], unassignedLeft: boolean, container: HTMLElement, ctx: CloseoutContext, passive = false, basis: { totals: WorkerDayTotals; partial: boolean } | null = null): void {
  const day = attributeDay(ctx.cleans, date, ctx.roster)
  const form = el('form', { class: 'closeout-form' })
  if (!passive) ctx.beginEdit()
  else {
    let claimed = false
    const claim = (): void => {
      if (claimed) return
      claimed = true
      ctx.beginEdit()
    }
    form.addEventListener('focusin', claim)
    form.addEventListener('input', claim)
  }
  // A one-person round (Liam, 19 Oct 2026): one total, saved under the owner's own name, for the work no other total
  // covers (basis), with no worker list, no worker pay and no "your cut". The name stays in a select never shown.
  const alone = worksAlone()
  const whole = basis?.totals ?? wholeDayTotals(day)
  const logged = (w: string): WorkerDayTotals => (alone ? whole : totalsFor(day, w))
  const worker = el('select', { class: 'cell-input', 'aria-label': 'Close out worker' })
  if (alone) {
    const own = getMe()?.workerName?.trim() ?? ''
    worker.append(el('option', { value: own }, own))
    worker.value = own
  } else {
  // kimi D-10: only workers without a close-out yet — defaulting to one
  // who already closed guaranteed a 409 round-trip.
  for (const w of workersLeft) worker.append(el('option', { value: w }, w))
  if (unassignedLeft) worker.append(el('option', { value: UNASSIGNED }, '(unassigned work)'))
  // Default to somebody who actually WORKED that day and has no total yet.
  // The list is everyone the round has ever used, alphabetically, so without
  // this the form opened on a name who did nothing — the prefill read £0.00
  // and the total would have been filed against the wrong person.
  const didWork = workersLeft.find((w) => totalsFor(day, w).jobs > 0)
  if (didWork) worker.value = didWork
  else if (unassignedLeft && day.unassigned.jobs > 0) worker.value = UNASSIGNED
  }
  const gross = el('input', { class: 'cell-input money-input', placeholder: '0.00', required: 'required', 'aria-label': `Day's work value in ${currencyWord()}` })
  const cut = el('input', { class: 'cell-input money-input', placeholder: '0.00', 'aria-label': `Amount paid to the worker in ${currencyWord()}` })
  const submit = el('button', { type: 'submit', class: 'btn-primary' }, 'Save day total')
  const cancel = el('button', { type: 'button' }, 'Cancel')
  cancel.addEventListener('click', () => {
    // A save on its way frees the Diary itself when it lands; Cancel then would let a newer edit lose its hold.
    if (submit.disabled) return
    ctx.afterEdit()
    ctx.reload()
  })

  // "gross" reads as cash taken, but the field is the VALUE OF THE WORK
  // completed that day — labelled as such, semantics unchanged. Prefill from
  // the cleans logged for the SELECTED worker (unassigned work has its own
  // bucket), so the common case is a glance-and-confirm rather than typing a
  // number from memory — and any gap between the two is stated, per worker,
  // instead of silently stored.
  const prefillFor = (w: string): string => {
    const t = logged(w)
    return t.jobs > 0 ? (t.pence / 100).toFixed(2) : ''
  }
  let grossIsPrefill = true
  gross.value = prefillFor(worker.value)

  // "paid Sendi £" rather than "paid worker £", and it follows the select —
  // the name is what says who this total belongs to.
  const paidWho = el('span', {}, paidLabel(worker.value === UNASSIGNED ? '' : worker.value))
  worker.addEventListener('change', () => { paidWho.textContent = paidLabel(worker.value === UNASSIGNED ? '' : worker.value) })

  // Changing the worker re-bases the prefill. A figure the user typed is
  // asked about before it is replaced; declining keeps what they typed.
  worker.addEventListener('change', () => {
    const next = prefillFor(worker.value)
    const who = worker.value || 'the unassigned work'
    let decision = prefillDecision(gross.value, grossIsPrefill, next)
    if (decision === 'ask') {
      decision = window.confirm(
        `You typed ${currencySymbol()}${gross.value.trim()} for the previous worker. ${who[0].toUpperCase()}${who.slice(1)} logged ${currencySymbol()}${next} — replace your figure with that?`,
      )
        ? 'replace'
        : 'keep'
    }
    if (decision === 'keep' && !next && gross.value.trim()) {
      window.alert(`No completed cleans are logged for ${who}. Your typed ${currencySymbol()}${gross.value.trim()} is still here — check it belongs to this worker before saving.`)
    }
    if (decision === 'replace') {
      gross.value = next
      grossIsPrefill = true
    }
    recalc()
  })

  const split = el('span', { class: 'closeout-split', 'aria-live': 'polite' })
  const statusNote = el('span', { class: 'closeout-status', 'aria-live': 'polite' })
  const recalc = (): void => {
    statusNote.textContent = paymentStatusNote(logged(worker.value)) ?? ''
    let g: number | null = null
    let c: number | null = null
    try {
      g = parseMoney(gross.value)
      c = parseMoney(cut.value)
    } catch {
      split.textContent = ' — check the amounts'
      split.className = 'closeout-split closeout-split-bad'
      return
    }
    if (g === null) {
      split.textContent = ''
      return
    }
    // A one-person round: only how the figure compares with the day's logged work.
    if (alone) {
      const v = varianceNote(g, whole)
      split.textContent = v ? ` · ${v.text}` : ''
      split.className = v?.warn ? 'closeout-split closeout-split-warn' : 'closeout-split'
      return
    }
    const ours = g - (c ?? 0)
    split.textContent = ` → your cut ${money(ours)}`
    split.className = ours < 0 ? 'closeout-split closeout-split-bad' : 'closeout-split'
    if (ours < 0) {
      split.textContent += " — the worker's cut is more than the day's work value"
    } else {
      const logged = totalsFor(day, worker.value)
      const v = varianceNote(g, logged)
      if (v) {
        split.textContent += ` · ${v.text}${worker.value ? ` for ${worker.value}` : ' for work with no worker recorded'}`
        if (v.warn) split.className = 'closeout-split closeout-split-warn'
      }
    }
    statusNote.textContent = paymentStatusNote(logged(worker.value)) ?? ''
  }
  gross.addEventListener('input', () => {
    grossIsPrefill = false
    recalc()
  })
  cut.addEventListener('input', recalc)
  recalc()

  form.append(
    el('span', { class: 'closeout-label' }, 'Day total'),
    // Said, so nobody reads the figure as the whole day (review, 19 Oct 2026).
    ...(alone && basis?.partial ? [el('span', { class: 'closeout-status' }, "This day already has a day total, so this one is only for the work it doesn't cover.")] : []),
    ...(alone ? [] : [worker]),
    el('label', { class: 'closeout-field' }, el('span', {}, `work done ${currencySymbol()}`), gross),
    ...(alone ? [] : [el('label', { class: 'closeout-field' }, paidWho, cut)]),
    split,
    submit,
    cancel,
    statusNote,
  )
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    let grossP: number | null
    let cutP: number | null
    try {
      grossP = parseMoney(gross.value)
      cutP = parseMoney(cut.value)
    } catch (err) {
      window.alert((err as Error).message)
      return
    }
    if (grossP === null) {
      window.alert("what was the day's work worth? (enter 0 if nothing)")
      return
    }
    if (cutP !== null && cutP > grossP) {
      window.alert(
        `the worker's ${money(cutP)} is more than the day's ${money(grossP)} work value — that would record a loss. Fix one of them, or write why in the note after saving.`,
      )
      return
    }
    submit.setAttribute('disabled', 'disabled')
    // A one-person round pays no worker: the whole day is kept.
    if (alone) cutP = 0
    const our = cutP === null ? null : grossP - cutP
    void mutate({
      label: `Close out ${date} (${worker.value || 'unassigned work'})`,
      method: 'POST',
      path: '/api/day-ledger',
      body: { date, worker: worker.value, gross_pence: grossP, worker_cut_pence: cutP, our_cut_pence: our },
      onAck: () => {
        ctx.afterEdit()
        ctx.reload()
      },
      onQueued: () => {
        ctx.afterEdit()
        ctx.reload()
      },
    }).then((ack) => {
      // 409 = someone already closed this date+worker — edit the row instead.
      if (!ack.ok && ack.status >= 400 && ack.status < 500) {
        ctx.afterEdit()
        window.alert(ack.error ?? 'rejected')
        ctx.reload()
      }
    })
  })
  container.append(form)
}

// Revising a close-out is a version-guarded PATCH (the API's POST is
// insert-only — a second close-out for the same date+worker is a 409).
function reviseLedgerForm(row: DayLedger, container: HTMLElement, ctx: CloseoutContext): void {
  ctx.beginEdit()
  const form = el('form', { class: 'closeout-form' })
  const gross = el('input', { class: 'cell-input money-input', required: 'required', 'aria-label': `Day's work value in ${currencyWord()}` })
  gross.value = row.gross_pence !== null ? (row.gross_pence / 100).toFixed(2) : ''
  const cut = el('input', { class: 'cell-input money-input', 'aria-label': `Amount paid to the worker in ${currencyWord()}` })
  cut.value = row.worker_cut_pence !== null ? (row.worker_cut_pence / 100).toFixed(2) : ''
  const notes = el('input', { class: 'cell-input', placeholder: 'note' })
  // Saved against the wrong person: pick the right worker here instead of
  // deleting and re-entering the day.
  const who = el('select', { class: 'cell-input', 'aria-label': 'Worker for this close-out' })
  for (const w of [...new Set([row.worker || '', ...ctx.workers])]) who.append(el('option', { value: w }, w || 'No worker'))
  who.value = row.worker || ''
  notes.value = row.notes ?? ''
  const submit = el('button', { type: 'submit', class: 'btn-primary' }, 'Save')
  const cancel = el('button', { type: 'button' }, 'Cancel')
  cancel.addEventListener('click', () => {
    // A save on its way frees the Diary itself when it lands; Cancel then would let a newer edit lose its hold.
    if (submit.disabled) return
    ctx.afterEdit()
    ctx.reload()
  })
  // A one-person round (Liam, 19 Oct 2026): no worker list, and worker pay only where some was recorded, so it can be
  // seen and changed. What is not shown is sent back as it was.
  const alone = worksAlone()
  form.append(
    ...(alone ? [] : [el('label', { class: 'closeout-field' }, el('span', {}, 'worker'), who)]),
    el('label', { class: 'closeout-field' }, el('span', {}, `work done ${currencySymbol()}`), gross),
    ...(alone && !row.worker_cut_pence ? [] : [el('label', { class: 'closeout-field' }, el('span', {}, paidLabel(row.worker)), cut)]),
    notes,
    submit,
    cancel,
  )
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    let grossP: number | null
    let cutP: number | null
    try {
      grossP = parseMoney(gross.value)
      cutP = parseMoney(cut.value)
    } catch (err) {
      window.alert((err as Error).message)
      return
    }
    if (grossP === null) {
      window.alert('work value is required')
      return
    }
    // The same guard the first close-out has. Without it, correcting a day
    // was the one way into the app to record paying the worker more than the
    // day earned — the typo that reads as a loss on the day's takings.
    if (cutP !== null && cutP > grossP) {
      window.alert(
        `the worker's ${money(cutP)} is more than the day's ${money(grossP)} work value — that would record a loss. Fix one of them, or write why in the note after saving.`,
      )
      return
    }
    submit.setAttribute('disabled', 'disabled')
    const our = cutP === null ? null : grossP - cutP
    void mutate({
      label: `Revise close-out ${row.date} (${row.worker || 'unassigned work'})`,
      method: 'PATCH',
      path: `/api/day-ledger/${row.id}`,
      body: {
        version: row.version,
        gross_pence: grossP,
        worker_cut_pence: cutP,
        our_cut_pence: our,
        notes: notes.value.trim() || null,
        ...(who.value !== (row.worker || '') ? { worker: who.value } : {}),
      },
      onAck: (ack) => {
        if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? 'rejected')
        ctx.afterEdit()
        ctx.reload()
      },
      onConflict: () => {
        ctx.afterEdit()
        window.alert('this close-out was edited elsewhere — reloading')
        ctx.reload()
      },
      onQueued: () => {
        ctx.afterEdit()
        ctx.reload()
      },
    }).then((ack) => {
      if (!ack.ok && ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
        ctx.afterEdit()
        window.alert(ack.error ?? 'rejected')
        ctx.reload()
      }
    })
  })
  container.append(form)
}

// "paid Sendi £" rather than "paid worker £" — the day total is read back
// weeks later and a name answers "who" without opening anything.
function paidLabel(worker: string | null | undefined): string {
  const name = (worker ?? '').trim()
  return name ? `paid ${name} ${currencySymbol()}` : `paid worker ${currencySymbol()}`
}

export function renderDayLedger(date: string, rows: DayLedger[], container: HTMLElement, ctx: CloseoutContext): void {
  container.textContent = ''
  const day = attributeDay(ctx.cleans, date, ctx.roster)
  // A one-person round (Liam, 19 Oct 2026): a total names nobody but someone else, and says worker pay only where some
  // was recorded. The totals themselves are as saved.
  const alone = worksAlone()
  const own = getMe()?.workerName?.trim() ?? ''
  for (const row of rows) {
    const line = el('div', { class: 'ledger-row cell-editable' })
    const named = (row.worker ?? '').trim()
    const other = alone && named.toLowerCase() !== own.toLowerCase() ? named : ''
    line.append(
      ...(alone && !other ? [] : [el('span', { class: 'badge badge-worker' }, alone ? other : row.worker || '?')]),
      // "gross £" read as cash taken; the field is the VALUE OF THE WORK
      // completed that day — the label says so, the stored meaning is
      // unchanged.
      el('span', {}, ` Day total · work done ${money(row.gross_pence)}`),
      ...(alone && !row.worker_cut_pence ? [] : [el('span', { class: 'ledger-cuts' }, ` · ${(row.worker ?? '').trim() || 'worker'} paid ${money(row.worker_cut_pence)} · kept ${money(row.our_cut_pence)}`)]),
    )
    if (row.notes) line.append(el('span', { class: 'clean-note' }, ` ${row.notes}`))
    line.title = 'tap to revise'
    line.addEventListener('click', () => {
      // Ledger rows ignored the editing lock and APPENDED, so tapping twice
      // (or tapping a row while a close-out form was open) left two competing
      // forms with no sign which one was authoritative.
      if (ctx.isEditing()) return
      reviseLedgerForm(row, container, ctx)
    })
    if (canRemoveCloseout(row, day)) {
      const remove = el('button', { class: 'closeout-btn' }, 'Remove')
      remove.addEventListener('click', event => {
        event.stopPropagation()
        if (ctx.isEditing()) return
        remove.setAttribute('disabled', 'disabled')
        void mutate({ label: `Remove empty day total ${date}`, method: 'DELETE', path: `/api/day-ledger/${row.id}`,
          body: { version: row.version }, onAck: ack => { alert4xx(ack); ctx.reload() },
          onConflict: () => ctx.reload(), onQueued: () => ctx.reload() }).then(ack => {
            if (!ack.ok && ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
              alert4xx(ack); ctx.reload()
            }
          })
      })
      line.append(remove)
    }
    container.append(line)
  }
  // Never "N jobs have no worker. Give them to me?" in a one-person round: work with no name is theirs already.
  const visits = alone ? [] : unassignedVisits(ctx.cleans, date, ctx.roster)
  const choices = visits.length ? assignmentChoices(date, rows, ctx) : []
  if (choices.length) {
    const phrase = visits.length === 1 ? 'Give it to' : 'Give them to'
    const owner = ownerDoesJobs() ? getMe()?.workerName?.trim().toLowerCase() : undefined
    const label = (name: string) => name.toLowerCase() === owner ? 'me' : name
    container.append(el('p', {}, `${visits.length} job${visits.length === 1 ? ' has' : 's have'} no worker.${choices.length === 1 ? ` ${phrase} ${label(choices[0])}?` : ` ${phrase}:`}`))
    const controls: (HTMLButtonElement | HTMLSelectElement)[] = []
    const giveTo = (name: string) => {
      if (ctx.isEditing()) return
      ctx.beginEdit()
      for (const control of controls) control.disabled = true
      void giveVisits(visits, name).finally(() => { ctx.afterEdit(); ctx.reload() })
    }
    const buttonNames = choices.length > 6 ? choices.slice(0, 5) : choices
    for (const name of buttonNames) {
      const give = el('button', { class: 'closeout-btn' }, `${phrase} ${label(name)}`)
      controls.push(give)
      give.addEventListener('click', () => giveTo(name))
      container.append(give)
    }
    if (choices.length > 6) {
      const other = el('select', { class: 'closeout-btn', 'aria-label': `${phrase} another worker` })
      other.append(el('option', { value: '' }, 'Other…'))
      for (const name of choices.slice(5)) other.append(el('option', { value: name }, label(name)))
      controls.push(other)
      other.addEventListener('change', () => {
        if (ctx.isEditing()) { other.value = ''; return }
        if (other.value) giveTo(other.value)
      })
      container.append(other)
    }
  }
  // Close-out entry: for today + past days only (closing out the future is
  // nonsense), one per worker per day. Case-INSENSITIVE: the API's insert-only
  // rule keys on (date, worker), so "liam" vs "Liam" used to slip past this
  // filter and mint a duplicate ledger row for the same person's same day.
  // A blank-worker row closes the unassigned bucket the same way.
  const closed = new Set(rows.map((r) => (r.worker ?? '').trim().toLowerCase()))
  // A one-person round: one total, under the owner's name, never counting work twice (aloneDayBasis): the whole day
  // while the day has no total, else only the work no total covers, and nothing to add once every bit is covered.
  // Missing by Money's own rule, so a day finished with Finish my day is not missing and opens nothing by itself; its
  // "+ Day total" stays (review, 19 Oct 2026).
  if (alone) {
    if (!isPastOrToday(date) || closed.has(own.toLowerCase())) return
    const basis = aloneDayBasis(day, rows)
    if (basis.partial && basis.totals.jobs === 0) return
    const missing = date < todayISO() && aloneDayMissing(day, rows, (ctx.finished ?? []).filter((f) => f.date === date))
    if (missing) container.append(el('p', { class: 'closeout-missing' }, 'Day total · not entered yet · the Money tab is missing this day'))
    const btn = el('button', { class: 'closeout-btn' }, missing ? '+ Enter the day total' : '+ Day total')
    btn.addEventListener('click', () => {
      if (ctx.isEditing()) return
      btn.remove()
      closeoutForm(date, [own], false, container, ctx, false, basis)
    })
    container.append(btn)
    if (missing && !ctx.isEditing() && !ctx.autoOpened) {
      ctx.autoOpened = true
      btn.remove()
      closeoutForm(date, [own], false, container, ctx, true, basis)
    }
    return
  }
  const workersLeft = ctx.workers.filter((w) => !closed.has(w.toLowerCase()))
  const unassignedLeft = day.unassigned.jobs > 0 && !closed.has('')
  if (isPastOrToday(date) && (workersLeft.length > 0 || unassignedLeft)) {
    // Three states, because a day total that is merely POSSIBLE reads the same
    // as one that is MISSING, and the missing ones are what keep the Money tab
    // short of a month. A past day with work done on it and no total says so
    // in amber and opens its form; today just offers the button.
    // "Missing" is not "a total could be entered" — it is work that HAPPENED
    // with nobody's total against it. Keyed per worker, the way day_ledger is
    // keyed and the way the Money tab counts it: a day two people worked and
    // only one totalled is still missing one. Without this, a day already
    // totalled by the person who worked it shouted for a second total from
    // every OTHER worker the round has ever used.
    const uncovered = [...day.byWorker.entries()].some(([key, t]) => t.jobs > 0 && !closed.has(key))
      || (day.unassigned.jobs > 0 && !closed.has(''))
    const missing = uncovered && date < todayISO()
    if (missing) {
      container.append(el('p', { class: 'closeout-missing' }, 'Day total · not entered yet · the Money tab is missing this day'))
    }
    const btn = el('button', { class: 'closeout-btn' }, missing ? '+ Enter the day total' : '+ Day total')
    btn.addEventListener('click', () => {
      if (ctx.isEditing()) return
      btn.remove()
      closeoutForm(date, workersLeft, unassignedLeft, container, ctx)
    })
    container.append(btn)
    // Opened for him, but only one at a time, and without taking the editing
    // lock until he uses it (see closeoutForm's `passive`).
    if (missing && !ctx.isEditing() && !ctx.autoOpened) {
      ctx.autoOpened = true
      btn.remove()
      closeoutForm(date, workersLeft, unassignedLeft, container, ctx, true)
    }
  }
}
